import { Controller } from "@hotwired/stimulus"

// @-mention autocomplete for the comment textarea. Detects an @-token at the
// caret, fetches the mention candidate list once per connect, filters
// client-side, and inserts the chosen handle into the textarea on Enter or
// click. Arrow keys navigate the popup.
export default class extends Controller {
  static targets = ["input"]
  static values = { url: String }

  connect() {
    this.candidates = []
    this.candidatesLoaded = false
    this.activeIndex = 0
    this.popup = null
    this.tokenStart = null
    this.boundOutside = (e) => this.handleOutsideClick(e)
    document.addEventListener("click", this.boundOutside)

    this.inputTarget.addEventListener("input", this.handleInput.bind(this))
    this.inputTarget.addEventListener("keydown", this.handleKeydown.bind(this))
    this.inputTarget.addEventListener("blur", () => {
      // Allow click on suggestion to fire before tearing down.
      setTimeout(() => this.closePopup(), 120)
    })
  }

  disconnect() {
    document.removeEventListener("click", this.boundOutside)
    this.closePopup()
  }

  async ensureCandidates() {
    if (this.candidatesLoaded) return
    if (!this.urlValue) return
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok) return
      const data = await response.json()
      this.candidates = Array.isArray(data.candidates) ? data.candidates : []
      this.candidatesLoaded = true
    } catch (_e) {
      this.candidates = []
    }
  }

  async handleInput() {
    const token = this.currentToken()
    if (!token) {
      this.closePopup()
      return
    }
    await this.ensureCandidates()
    const filtered = this.filterCandidates(token.query)
    if (filtered.length === 0) {
      this.closePopup()
      return
    }
    this.tokenStart = token.start
    this.openPopup(filtered)
  }

  handleKeydown(event) {
    if (!this.popup) return
    const items = this.popup.querySelectorAll("li")
    if (items.length === 0) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.activeIndex = (this.activeIndex + 1) % items.length
      this.refreshActive(items)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.activeIndex = (this.activeIndex - 1 + items.length) % items.length
      this.refreshActive(items)
    } else if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault()
      const handle = items[this.activeIndex]?.dataset?.handle
      if (handle) this.applyHandle(handle)
    } else if (event.key === "Escape") {
      this.closePopup()
    }
  }

  currentToken() {
    const value = this.inputTarget.value
    const caret = this.inputTarget.selectionStart ?? value.length
    if (caret === 0) return null
    const upToCaret = value.slice(0, caret)
    // Everything after the last @ up to the caret (may include inner @ for emails like @x@test.com).
    const match = upToCaret.match(/(^|[\s])@([^\s]*)$/)
    if (!match) return null
    const query = match[2].toLowerCase()
    const start = caret - match[2].length - 1 // include the leading "@"
    return { start, query }
  }

  filterCandidates(query) {
    if (query === "") return this.candidates.slice(0, 8)
    const q = query.toLowerCase()
    return this.candidates
      .filter((c) => {
        const h = (c.handle || "").toLowerCase()
        const l = (c.label || "").toLowerCase()
        return h === q || h.startsWith(q) || l.startsWith(q) || l.includes(q)
      })
      .slice(0, 8)
  }

  openPopup(items) {
    if (!this.popup) {
      this.popup = document.createElement("ul")
      this.popup.className =
        "absolute z-30 mt-1 max-h-56 w-72 overflow-auto rounded-md border border-zinc-200 bg-white text-sm shadow-lg"
      this.popup.setAttribute("role", "listbox")
      this.element.style.position = this.element.style.position || "relative"
      this.element.appendChild(this.popup)
    }

    this.popup.innerHTML = ""
    this.activeIndex = 0
    items.forEach((item, idx) => {
      const li = document.createElement("li")
      li.dataset.handle = item.handle
      li.className = `cursor-pointer px-3 py-1.5 ${idx === 0 ? "bg-zinc-100" : ""}`
      li.setAttribute("role", "option")
      li.innerHTML = `<span class="font-medium text-jpm-accent">@${item.handle}</span><span class="ml-2 text-xs text-zinc-500">${item.label} · ${item.kind}</span>`
      li.addEventListener("mousedown", (e) => {
        e.preventDefault()
        this.applyHandle(item.handle)
      })
      this.popup.appendChild(li)
    })
  }

  refreshActive(items) {
    items.forEach((li, idx) => {
      li.classList.toggle("bg-zinc-100", idx === this.activeIndex)
    })
  }

  applyHandle(handle) {
    if (this.tokenStart == null) return
    const value = this.inputTarget.value
    const caret = this.inputTarget.selectionStart ?? value.length
    const before = value.slice(0, this.tokenStart)
    const after = value.slice(caret)
    const insertion = `@${handle} `
    this.inputTarget.value = `${before}${insertion}${after}`
    const newCaret = before.length + insertion.length
    this.inputTarget.setSelectionRange(newCaret, newCaret)
    this.inputTarget.focus()
    this.closePopup()
  }

  closePopup() {
    if (this.popup) {
      this.popup.remove()
      this.popup = null
    }
    this.tokenStart = null
    this.activeIndex = 0
  }

  handleOutsideClick(event) {
    if (!this.popup) return
    if (this.element.contains(event.target)) return
    this.closePopup()
  }
}
